/**
 * Import d'un export **Clipperz** HTML (« Data > Export > HTML » : une page
 * dont le `<textarea>` porte le coffre en JSON) : ses utilisateurs doivent
 * pouvoir partir avec TOUT leur coffre.
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le JSON est un tableau de fiches `{ label, data: { notes },
 * currentVersion: { fields: { <id>: { label, value, hidden, actionType } } } }`.
 * Le libelle porte le titre puis les etiquettes, chacune precedee de U+E009 ;
 * une etiquette speciale (U+E009 U+E010) `ARCH` classe la fiche aux archives.
 *
 * Au-dela de Bitwarden, qui coupe le libelle au premier espace (« Bank of
 * America » devient « Bank ») et garde le DERNIER mot de passe d'une fiche :
 * le titre est repris entier, ses etiquettes en etiquettes, le premier mot de
 * passe en mot de passe et les suivants en champs masques ; un champ que
 * Clipperz masque reste masque.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, type ImportedEntry } from './csv-import.js';
import { normalizeTags } from './entry-tags.js';
import { decodeEntities } from './kdbx-import.js';
import { serverHost } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

/** Marque d'etiquette du libelle Clipperz, et marque d'etiquette speciale (archive). */
const TAG_CHAR = '\uE009';
const SPECIAL_TAG_CHAR = '\uE010';

/** Libelles de champ reconnus quand Clipperz ne dit pas le role du champ (`actionType` TEXT). */
const PASSWORD_LABELS = new Set(['password', 'pass', 'passwd', 'pwd', 'mot de passe']);
const USERNAME_LABELS = new Set(['username', 'user name', 'user', 'login', 'email', 'e-mail', 'mail', 'identifiant', 'utilisateur']);
const URL_LABELS = new Set(['url', 'uri', 'web site', 'website', 'site', 'address', 'adresse', 'hostname', 'host']);

/** Titre, etiquettes et archivage tires du libelle `Titre U+E009etiquette U+E009U+E010ARCH`. */
function splitLabel(label: string): { name: string; tags: string[]; archived: boolean } {
  const [title = '', ...tokens] = label.split(TAG_CHAR);
  const tags: string[] = [];
  let archived = false;
  for (const token of tokens) {
    const tag = token.trim();
    if (tag.startsWith(SPECIAL_TAG_CHAR)) {
      if (tag.slice(1) === 'ARCH') archived = true;
    } else if (tag.length > 0) {
      tags.push(tag);
    }
  }
  return { name: title.trim(), tags: normalizeTags(tags), archived };
}

/** Une fiche Clipperz -> une entree (identifiant, ou note si elle ne porte ni compte ni adresse). */
function parseClipperzCard(card: Record<string, Json>): ImportedEntry | null {
  const { name, tags, archived } = splitLabel(asString(card['label']));
  let username = '';
  let password = '';
  const uris: string[] = [];
  const customFields: EntryCustomField[] = [];
  const fields = asObject(asObject(card['currentVersion'])?.['fields']) ?? {};
  for (const raw of Object.values(fields)) {
    const field = asObject(raw);
    if (field === null) continue;
    const label = asString(field['label']);
    const value = rawSecret(field['value']);
    if (value.trim().length === 0) continue;
    const action = asString(field['actionType']).toLowerCase();
    const known = label.toLowerCase();
    const role =
      action === 'password' || (action !== 'url' && action !== 'email' && PASSWORD_LABELS.has(known))
        ? 'password'
        : action === 'email' || USERNAME_LABELS.has(known)
          ? 'username'
          : action === 'url' || URL_LABELS.has(known)
            ? 'url'
            : 'field';
    if (role === 'password' && password.length === 0) {
      password = value;
    } else if (role === 'username' && username.length === 0) {
      username = value.trim();
    } else if (role === 'url') {
      if (!uris.includes(value.trim())) uris.push(value.trim());
    } else {
      // Mot de passe de plus, compte de plus ou champ libre : garde en champ, masque si Clipperz le masque.
      const kept = role === 'password' || field['hidden'] === true ? value : value.trim();
      const base = csvColumnField(label || 'Champ', kept);
      customFields.push(role === 'password' || field['hidden'] === true ? { ...base, hidden: true } : base);
    }
  }

  // Clipperz double l'echappement des sauts de ligne de ses notes (`\\n` dans le JSON), comme le lit Bitwarden.
  const rawNotes = asString(asObject(card['data'])?.['notes']);
  const notes = joinNotes([rawNotes.split('\\n').join('\n').replace(/\r\n?/gu, '\n').trim()]);
  const isNote = username.length === 0 && password.length === 0 && uris.length === 0;
  if (isNote && name.length === 0 && notes.length === 0 && customFields.length === 0) return null;
  const host = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
  return {
    type: isNote ? 'note' : 'login',
    name: name || host || username || 'Sans nom',
    username,
    password,
    uris,
    notes,
    totpSecret: '',
    favorite: false,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(archived ? { archived: true } : {}),
  };
}

/** Le tableau JSON du `<textarea>` d'une page d'export Clipperz, ou `null` si le texte n'en est pas une. */
function clipperzCards(text: string): readonly Json[] | null {
  if (!/^[\s\uFEFF]*</u.test(text)) return null;
  const textarea = /<textarea\b[^>]*>([\s\S]*?)<\/textarea\s*>/iu.exec(text);
  if (textarea === null) return null;
  let cards: unknown;
  try {
    cards = JSON.parse(decodeEntities(textarea[1]!));
  } catch {
    return null;
  }
  // Chaque fiche porte un libelle et une version courante : c'est la signature de Clipperz.
  return Array.isArray(cards) &&
    cards.every((card) => typeof asObject(card)?.['label'] === 'string' && asObject(asObject(card)?.['currentVersion']) !== null)
    ? cards
    : null;
}

/** Un export Clipperz se reconnait a sa page HTML dont le `<textarea>` porte un tableau de fiches. */
export function isClipperzHtml(text: string): boolean {
  return clipperzCards(text) !== null;
}

/** Parse un export Clipperz HTML : une fiche = une entree. */
export function parseClipperzHtml(text: string): ImportedEntry[] {
  const cards = clipperzCards(text);
  if (cards === null) throw new Error('export Clipperz : structure inattendue');
  return cards
    .map(asObject)
    .filter((card): card is Record<string, Json> => card !== null)
    .map(parseClipperzCard)
    .filter((entry): entry is ImportedEntry => entry !== null);
}
